import React from 'react'
import { ControlledModal } from '../../layouts/components/modal'
import { Teacher, useAppContext } from '../../providers/context'
import { makeRequest, showAxiosErrorAsToast } from '../../request'
import { Button, ButtonShape, ButtonStyle } from '../../components/simple/button'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { getPresignedUrl, inputSingleFile, uploadToS3 } from '../../components/media/fileUpload'
import { IconTimes } from '../../icons/library'

export const DistrictSettingsModal: React.FC<{
  user: Teacher
  onClose: () => void
}> = ({ user, onClose }) => {
  const { reloadUser } = useAppContext()
  const [loading, setLoading] = React.useState(false)
  const [showAllRecipients, setShowAllRecipients] = React.useState(user.district.showAllRecipients)
  const [showForwardingOptions, setShowForwardingOptions] = React.useState(user.district.showForwardingOptions)
  const [newLogoName, setNewLogoName] = React.useState<string | undefined>(undefined)
  const [logoUploading, setLogoUploading] = React.useState(false)
  const [logoPreviewUrl, setLogoPreviewUrl] = React.useState<string | undefined>(undefined)
  const [schoolLogoNames, setSchoolLogoNames] = React.useState<Record<number, string | undefined>>({})
  const [schoolLogoPreviewUrls, setSchoolLogoPreviewUrls] = React.useState<Record<number, string | undefined>>({})
  const [schoolLogoUploading, setSchoolLogoUploading] = React.useState<Record<number, boolean>>({})

  // Clean up preview URLs when component unmounts
  React.useEffect(() => {
    return () => {
      if (logoPreviewUrl) {
        URL.revokeObjectURL(logoPreviewUrl)
      }
      Object.values(schoolLogoPreviewUrls).forEach((url) => {
        if (url) URL.revokeObjectURL(url)
      })
    }
  }, [logoPreviewUrl, schoolLogoPreviewUrls])

  return (
    <ControlledModal
      size="lg"
      onOpenChange={(o) => {
        if (!o) onClose()
      }}
      open={true}
      title={user.districtAccess ? 'Edit District' : 'Edit School'}
      disabled={loading}
    >
      <div className="flex flex-col gap-1">
        Should users see only other users in their classes or should they see everyone in their school? Admins are not
        affected.
        <LabeledCheckbox checked={showAllRecipients} onChecked={setShowAllRecipients} label="Show all users" />
      </div>
      <div className="mt-3 flex flex-col gap-1">
        Should teachers be able to disable forwarded message notifications
        <LabeledCheckbox
          checked={showForwardingOptions}
          onChecked={setShowForwardingOptions}
          label="Show forwarding options"
        />
      </div>
      <DistrictLogoUpload
        logoLink={user.district.logoLink}
        uploadDisabled={loading}
        logoUploading={logoUploading}
        setLogoUploading={setLogoUploading}
        setNewLogoName={setNewLogoName}
        setLogoPreviewUrl={setLogoPreviewUrl}
        newLogoName={newLogoName}
        logoPreviewUrl={logoPreviewUrl}
      />

      {/* School Logos Section */}
      {user.districtAccess && user.admin >= 2 && (
        <div className="mt-6">
          <h3 className="mb-4 text-lg font-semibold">School Logos</h3>
          <div className="space-y-4">
            {user.district.schools.map((school) => (
              <SchoolLogoUpload
                key={school.id}
                school={school}
                uploadDisabled={loading}
                logoUploading={schoolLogoUploading[school.id] || false}
                setLogoUploading={(uploading) =>
                  setSchoolLogoUploading((prev) => ({ ...prev, [school.id]: uploading }))
                }
                setNewLogoName={(logoName) => setSchoolLogoNames((prev) => ({ ...prev, [school.id]: logoName }))}
                setLogoPreviewUrl={(url) => setSchoolLogoPreviewUrls((prev) => ({ ...prev, [school.id]: url }))}
                newLogoName={schoolLogoNames[school.id]}
                logoPreviewUrl={schoolLogoPreviewUrls[school.id]}
              />
            ))}
          </div>
        </div>
      )}

      {(newLogoName || Object.keys(schoolLogoNames).length > 0) && (
        <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-600">
          <b>Press "Save"</b> to apply your logo changes.
        </div>
      )}

      <Button
        logo
        disabled={loading || logoUploading || Object.values(schoolLogoUploading).some(Boolean)}
        className="mt-4 self-start"
        onClick={async () => {
          setLoading(true)

          // Use regular JSON request - files are already uploaded to temp via presigned URLs
          makeRequest<void>('/district/edit', {
            showallrecipients: showAllRecipients,
            showforwardingoptions: showForwardingOptions,
            newLogoName,
            schoolLogoNames,
          })
            .then(async () => {
              // Clean up preview URLs
              if (logoPreviewUrl) {
                URL.revokeObjectURL(logoPreviewUrl)
                setLogoPreviewUrl(undefined)
              }
              Object.values(schoolLogoPreviewUrls).forEach((url) => {
                if (url) URL.revokeObjectURL(url)
              })
              setSchoolLogoPreviewUrls({})

              // Reset logo states so the new logos from reloaded user data are shown
              setNewLogoName(undefined)
              setSchoolLogoNames({})
              // Wait for user data to reload before closing modal
              await reloadUser()
              onClose()
            })
            .catch(showAxiosErrorAsToast)
            .finally(() => setLoading(false))
        }}
      >
        Save
      </Button>
    </ControlledModal>
  )
}

export const SchoolLogoUpload: React.FC<{
  school: { id: number; name: string; logoLink?: string }
  uploadDisabled: boolean
  logoUploading: boolean
  setLogoUploading: (uploading: boolean) => void
  setNewLogoName: (name: string | undefined) => void
  setLogoPreviewUrl: (url: string | undefined) => void
  newLogoName?: string
  logoPreviewUrl?: string
}> = ({
  school,
  uploadDisabled,
  logoUploading,
  setLogoUploading,
  setNewLogoName,
  setLogoPreviewUrl,
  newLogoName,
  logoPreviewUrl,
}) => (
  <div className="flex items-center justify-between gap-4 rounded-lg border p-3">
    <div className="flex flex-col gap-2">
      <b>{school.name}</b>
      <Button
        onClick={async () => {
          setLogoUploading(true)
          const file = await inputSingleFile('image/png, image/jpeg, image/gif, image/jpg')
          if (file) {
            try {
              // Create a preview URL for immediate display
              const previewUrl = URL.createObjectURL(file)
              setLogoPreviewUrl(previewUrl)

              const url = await getPresignedUrl(file.name)
              await uploadToS3(url, file)
              setNewLogoName(file.name)
            } catch (e) {
              showAxiosErrorAsToast(e)
              // Clean up preview URL on error
              if (logoPreviewUrl) {
                URL.revokeObjectURL(logoPreviewUrl)
                setLogoPreviewUrl(undefined)
              }
            }
          }
          setLogoUploading(false)
        }}
        disabled={uploadDisabled || logoUploading}
        showLoader={logoUploading}
        className="self-start"
      >
        {logoUploading ? 'Uploading' : school.logoLink || newLogoName ? 'Change logo' : 'Upload logo'}
      </Button>
      {newLogoName && <div className="text-xs text-gray-600">New logo: {newLogoName}</div>}
    </div>
    {logoPreviewUrl ? (
      <img
        src={logoPreviewUrl}
        className="max-h-[40px] max-w-[200px] object-contain shadow-even"
        height="40px"
        alt={`${school.name} logo (preview)`}
      />
    ) : school.logoLink && !newLogoName ? (
      <img
        src={school.logoLink}
        className="max-h-[40px] max-w-[200px] object-contain shadow-even"
        height="40px"
        alt={`${school.name} logo`}
      />
    ) : newLogoName && newLogoName !== 'remove' ? (
      <div className="flex items-center gap-2">
        <div className="flex h-[40px] w-[40px] items-center justify-center bg-blue-100 text-xs shadow-even">
          uploaded
        </div>
        <span className="text-xs text-gray-600">New logo uploaded</span>
      </div>
    ) : (
      <div className="flex h-[40px] w-[40px] items-center justify-center bg-slate-100 text-xs shadow-even">no logo</div>
    )}
    {(school.logoLink || newLogoName) && (
      <Button
        buttonStyle={ButtonStyle.ghostRedTransparent}
        shape={ButtonShape.icon}
        tooltip="Remove logo"
        onClick={() => {
          setNewLogoName('remove')
        }}
      >
        <IconTimes />
      </Button>
    )}
  </div>
)

export const DistrictLogoUpload: React.FC<{
  logoLink?: string
  uploadDisabled: boolean
  logoUploading: boolean
  setLogoUploading: React.Dispatch<React.SetStateAction<boolean>>
  setNewLogoName: React.Dispatch<React.SetStateAction<string | undefined>>
  setLogoPreviewUrl: React.Dispatch<React.SetStateAction<string | undefined>>
  newLogoName?: string
  logoPreviewUrl?: string
}> = ({
  logoLink,
  uploadDisabled,
  logoUploading,
  setLogoUploading,
  setNewLogoName,
  setLogoPreviewUrl,
  newLogoName,
  logoPreviewUrl,
}) => (
  <div className="mt-4 flex items-center justify-between gap-4">
    <div className="flex flex-col gap-2">
      <b>District Logo</b>
      <Button
        onClick={async () => {
          setLogoUploading(true)
          const file = await inputSingleFile('image/png, image/jpeg, image/gif, image/jpg')
          if (file) {
            try {
              // Create a preview URL for immediate display
              const previewUrl = URL.createObjectURL(file)
              setLogoPreviewUrl(previewUrl)

              const url = await getPresignedUrl(file.name)
              await uploadToS3(url, file)
              setNewLogoName(file.name)
            } catch (e) {
              showAxiosErrorAsToast(e)
              // Clean up preview URL on error
              if (logoPreviewUrl) {
                URL.revokeObjectURL(logoPreviewUrl)
                setLogoPreviewUrl(undefined)
              }
            }
          }
          setLogoUploading(false)
        }}
        disabled={uploadDisabled || logoUploading}
        showLoader={logoUploading}
        className="self-start"
      >
        {logoUploading ? 'Uploading' : logoLink || newLogoName ? 'Change logo' : 'Upload logo'}
      </Button>
    </div>
    {logoPreviewUrl ? (
      <img
        src={logoPreviewUrl}
        className="max-h-[50px] max-w-[300px] object-contain shadow-even"
        height="50px"
        alt="District logo (preview)"
      />
    ) : logoLink && !newLogoName ? (
      <img
        src={logoLink}
        className="max-h-[50px] max-w-[300px] object-contain shadow-even"
        height="50px"
        alt="District logo"
      />
    ) : newLogoName && newLogoName !== 'remove' ? (
      <div className="flex items-center gap-2">
        <div className="flex h-[50px] w-[50px] items-center justify-center bg-blue-100 text-xs shadow-even">
          uploaded
        </div>
        <span className="text-sm text-gray-600">New logo uploaded - save to apply</span>
      </div>
    ) : (
      <div className="flex h-[50px] w-[50px] items-center justify-center bg-slate-100 text-sm shadow-even">no logo</div>
    )}
    {logoLink && !newLogoName && (
      <Button
        buttonStyle={ButtonStyle.ghostRedTransparent}
        shape={ButtonShape.icon}
        tooltip="Remove logo"
        onClick={() => {
          setNewLogoName('remove')
        }}
      >
        <IconTimes />
      </Button>
    )}
  </div>
)
